/** @jsxImportSource @emotion/react */
import {
  Header,
  TruncatedString,
  formatCurrency,
} from '@quickbit/backoffice-core-frontend';
import {
  Card,
  Icon,
  Input,
  Loader,
  Table,
  Typography,
  useTheme,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { capitalize, kebabCase } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import { useDetails, useRestart } from 'api/useDirectOnrampApi';
import { CustodianTransactionTable } from 'tabs/DirectOnramp/components';

export const TransactionRequestDetails = () => {
  const { palette, spacing } = useTheme();
  const navigate = useNavigate();
  const { referenceId } = useParams();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { data, isLoading } = useDetails(referenceId!);

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { mutate: restart, isPending: isRestarting } = useRestart(referenceId!);
  return (
    <>
      <Header>
        <div
          css={{
            display: 'flex',
            gap: spacing.xxxs,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div
            css={{ display: 'flex', gap: spacing.xxxs, alignItems: 'center' }}
          >
            <div
              css={{
                cursor: 'pointer',
                ':hover': {
                  textDecoration: 'underline',
                  textDecorationColor: palette.grey.x300,
                },
              }}
              onClick={() => navigate('/direct-onramp/transaction-requests')}
            >
              <Typography variant="subtitle" color={palette.grey.x300}>
                Transaction Requests
              </Typography>
            </div>
            <div
              css={{
                transform: 'rotate(180deg)',
                svg: {
                  fill: 'none',
                  stroke: palette.grey.x300,
                },
              }}
            >
              <Icon name="chevron" />
            </div>
            <Typography variant="subtitle">{referenceId}</Typography>
          </div>
          <UtilityButton
            text="Restart"
            onClick={() => restart()}
            disabled={isRestarting}
            loading={isRestarting}
          />
        </div>
      </Header>
      {isLoading && <Loader />}
      {data && (
        <div>
          <div
            css={{
              display: 'grid',
              gap: spacing.xxs,
              gridTemplateColumns: 'repeat(2, 1fr)',
            }}
          >
            {Object.entries(data)
              .filter(
                ([key]) =>
                  !key.includes('Transaction') && !key.includes('history')
              )
              .map(([key, value]) => (
                <div
                  key={key}
                  css={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: palette.black,
                    },
                  }}
                >
                  <Input
                    label={capitalize(kebabCase(key).replace(/-/g, ' '))}
                    value={
                      value instanceof Date
                        ? value.toLocaleString()
                        : // eslint-disable-next-line @typescript-eslint/no-base-to-string
                          value?.toString() ?? ''
                    }
                    disabled
                    endAdornment={
                      key === 'userId' && (
                        <UtilityButton
                          variant="icon"
                          icon="arrow"
                          css={{ svg: { transform: 'rotate(90deg)' } }}
                          onClick={() =>
                            navigate(`/direct-onramp/users/${value as string}`)
                          }
                        />
                      )
                    }
                  />
                </div>
              ))}
          </div>
          {data.brokerTransaction && (
            <Card css={{ marginTop: spacing.xxs }}>
              <Typography variant="subtitle" color={palette.grey.x300}>
                Broker Transaction
              </Typography>

              <Table
                headers={[
                  { label: 'Created At', cellKey: 'createdAt', sort: false },
                  {
                    label: 'Transaction Code',
                    cellKey: 'transactionCode',
                    sort: false,
                  },
                  {
                    label: 'Account Code',
                    cellKey: 'accountCode',
                    sort: false,
                  },
                  { label: 'Amount', cellKey: 'amount', sort: false },
                  { label: 'Euro Amount', cellKey: 'euroAmount', sort: false },
                ]}
                rows={[
                  {
                    value: {
                      createdAt:
                        data.brokerTransaction.createdAt.toLocaleString(),
                      transactionCode: (
                        <TruncatedString
                          text={data.brokerTransaction.transactionCode}
                        />
                      ),
                      accountCode: data.brokerTransaction.accountCode,
                      amount: formatCurrency(data.brokerTransaction.amount),
                      euroAmount: formatCurrency({
                        amount: data.brokerTransaction.euroAmount,
                        currencyCode: 'EUR',
                        scale: 0,
                      }),
                    },
                  },
                ]}
              />
            </Card>
          )}
          {data.custodianTransactions.length > 0 && (
            <Card css={{ marginTop: spacing.xxs }}>
              <Typography variant="subtitle" color={palette.grey.x300}>
                Custodian Transactions
              </Typography>

              <CustodianTransactionTable
                transactions={data.custodianTransactions}
              />
            </Card>
          )}
          {data.history.length > 0 && (
            <Card css={{ marginTop: spacing.xxs }}>
              <Typography variant="subtitle" color={palette.grey.x300}>
                History
              </Typography>

              <Table
                headers={[
                  { label: 'Created At', cellKey: 'createdAt', sort: false },
                  { label: 'State', cellKey: 'state', sort: false },
                ]}
                rows={data.history.map((history) => ({
                  value: {
                    createdAt: `${history.createdAt.toLocaleString()} ${history.createdAt.getMilliseconds()}`,
                    state: history.state,
                  },
                }))}
              />
            </Card>
          )}
        </div>
      )}
    </>
  );
};
